import React from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl from 'maplibre-gl';
import {Map, Source, Layer, ScaleControl, GeolocateControl, NavigationControl, Popup} from 'react-map-gl/maplibre';
import {buildParams} from './helper';
import styled from 'styled-components';
import SharedSearchResultInterface from "./SharedSearchResultInterface";

const SearchMap = (props) => {
    const {filterParams} = props;
    const [workplacePopup, setWorkplacePopup] = React.useState(null);
    const [cursor, setCursor] = React.useState('auto');
    const [totalResults, setTotalResults] = React.useState();
    const [pointsOfInterest, setPointsOfInterest] = React.useState({'features': [], type: 'FeatureCollection'});
    const [radiusSearchCircle, setRadiusSearchCircle] = React.useState(null);

    React.useEffect(() => {
        // update circle data if required
        if ((props.filterParams.lat && props.filterParams.lon) && (!radiusSearchCircle || radiusSearchCircle.lat !== props.filterParams.lat || radiusSearchCircle.lon !== props.filterParams.lon )) {
            setRadiusSearchCircle({lat: props.filterParams.lat, lon: props.filterParams.lon});
        }
        if ((!props.filterParams.lat || !props.filterParams.lon) && radiusSearchCircle) {
             setRadiusSearchCircle(null);
        }

        // get data
        fetch(
            props.settings.wpFastsearchServer
            + '/api/search/'
            + props.settings.wpFastsearchToken
            + '/workplace/geo/location?'
            + buildParams(filterParams),
        ).then((response) => {
            response.json().then((parsed) => {
                setPointsOfInterest(parsed);
                setTotalResults(parsed.features.length);
            });
        });
    }, [props.filterParams]);

    const radiusSearchCircleGeoJSON = {
        type: 'FeatureCollection',
        features: [
            ...(radiusSearchCircle === null ? [] : [{
                type: 'Feature',
                properties: {
                    lat: radiusSearchCircle.lat,
                    radius: filterParams.radius,
                },
                geometry: {
                    type: 'Point',
                    coordinates: [radiusSearchCircle.lon, radiusSearchCircle.lat],
                },
            }]),
        ],
    }

    const handleOnMouseEnter = React.useCallback(() => setCursor('pointer'), []);
    const handleOnMouseLeave = React.useCallback(() => setCursor('auto'), []);
    const handleOnClick = evt => {
        if (!evt.features || evt.features.length === 0)
            return;
        setWorkplacePopup({
            lat: evt.lngLat.lat,
            lon: evt.lngLat.lng,
            features: evt.features.map(feature => feature.properties),
        });
    }

    const workplacesLayerStyle = {
        id: 'workplaces-icons',
        type: 'circle',
        source: 'workplaces',
        paint: {
            'circle-radius': 7,
            'circle-color': '#29DD41FF',
            "circle-stroke-width": 1,
            "circle-stroke-color": '#025501',
        },
        cursor: 'pointer',
    };

    const radiusSearchCircleLayerStyle = {
        id: 'radius-search-circle-icons',
        type: 'circle',
        source: 'radius-search-circle',
        paint: {
            'circle-radius': [
                'interpolate',
                ['exponential', 2],
                ['zoom'],
                0, 0,
                20, [
                    '/',
                    ['/', ['get', 'radius'], 0.075],
                    ['cos', ['*', ['get', 'lat'], ['/', Math.PI, 180]]],
                ],
            ],
            'circle-color': 'rgba(41, 221, 65, 0.05)',
            "circle-stroke-width": 1,
            "circle-stroke-color": 'rgba(2, 85, 1, 0.5)',
        },
        cursor: 'pointer',
    }

    return <>
        <SharedSearchResultInterface totalResults={totalResults}
                                     settings={props.settings}
                                     defaultFilterParams={props.defaultFilterParams}
                                     filterParams={props.filterParams}
                                     setFilterParams={props.setFilterParams}
                                     currentViewIsMap={props.currentViewIsMap}
                                     setCurrentViewIsMap={props.setCurrentViewIsMap}
                                     isSearchFormVisible={props.isSearchFormVisible}
                                     setIsSearchFormVisible={props.setIsSearchFormVisible} />
        <MapContainer>
            <Map mapLib={maplibregl}
                 mapStyle="https://tiles.binary-butterfly.de/osm-bright-gl-style/style.json"
                 initialViewState={{
                     longitude: props.settings.defaultMapCenter[0],
                     latitude: props.settings.defaultMapCenter[1],
                     zoom: props.settings.defaultMapZoom,
                     pitch: 0,
                     bearing: 0,
                 }}
                 cooperativeGestures
                 cursor={cursor}
                 style={{height: 900}}
                 interactiveLayerIds={['workplaces-icons']}
                 onClick={handleOnClick}
                 onMouseEnter={handleOnMouseEnter}
                 onMouseLeave={handleOnMouseLeave}
                 onZoomStart={() => setWorkplacePopup(null)} // because then popups often move too much
            >
                <GeolocateControl position="top-left" />
                <NavigationControl position="top-left" />
                <ScaleControl />
                <Source id="radius-search-circle" type="geojson" data={radiusSearchCircleGeoJSON}>
                    <Layer {...radiusSearchCircleLayerStyle} />
                </Source>
                <Source id="workplaces" type="geojson" data={pointsOfInterest}>
                    <Layer {...workplacesLayerStyle} />
                </Source>
                {workplacePopup && (
                    <Popup
                        longitude={Number(workplacePopup.lon)}
                        latitude={Number(workplacePopup.lat)}
                        onClose={() => setWorkplacePopup(null)}
                    >
                        <ul>
                            {workplacePopup.features.map((feature, i) => <li key={i}>
                                <a href={feature.url} target={'_blank'}>{feature.title}</a>
                            </li>)}
                        </ul>
                    </Popup>
                )}
            </Map>
        </MapContainer>
    </>;
};

const MapContainer = styled.div`
    & > div > ul {
        margin: 0 0 0 auto;

        & > li {
            padding-right: 2rem;
        }
    }

    & > div > div > button {
        background-color: white;
        color: black;
        padding: 0.25rem;
    }

    & .maplibregl-map {
        border-radius: 5px;
    }
`;

export default SearchMap;
